import React from 'react';
import map from 'lib/react/html/map';
import { observer } from 'mobx-react';
import { Site } from 'four-nets/site';
import * as PropTypes from 'prop-types';
import { Mutation } from '@apollo/client/react/components';

import Styled from 'styled';
import EnhancedText from 'four-nets/ui/enhanced-text/enhanced-text';
import Localizable from 'lib/intl/localizable';
import { FormattedMessage } from 'react-intl';

import GalleryView from 'four-nets/ui/gallery/gallery-view';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import { DeletedContent } from 'four-nets/common/deleted-content';
import { PurgedContent } from 'four-nets/common/purged-content';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';

import isPrArticleUsername from '../is-pr-article-username';
import mutation from './mutations/recover-message.graphql';
import photoswipeGalleryStyles from './photoswipe-gallery-view.scss';
import portraitGalleryStyles from './photoswipe-gallery-portrait-view.scss';
import defaultStyles from './comment-content-view.scss';
import { toGlobalId } from 'graphql-relay';

export class CommentContentViewModel {
    comment;

    constructor(options) {
        const { comment } = options;
        this.comment = comment;
    }
}

@Styled(defaultStyles)
@Localizable()
@observer
class CommentContentView extends React.Component {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    displayedContent = () => {
        const { displayedContent, user } = this.props.commentContentViewModel.comment;

        if (displayedContent instanceof DeletedContent) {
            const { site } = this.context;
            let message = this.props.intl.formatMessage(displayedContent.defineMessages, {
                rules: `<a target="_blank" style="color: ${site.isLiving ? '#003b71' : '#024d23'}" href="${
                    displayedContent.rulesLink.href
                }">${displayedContent.rulesLink.label}</a>`,
            });
            return map(`<span style="color: #b0bec5; font-weight: 300">${message}</span>`);
        } else if (displayedContent instanceof PurgedContent) {
            return <FormattedMessage id='FORUM_MESSAGE_PURGED' />;
        }
        return (
            <EnhancedText followLink={user?.vendor?.isPresentationPaid || isPrArticleUsername(user?.username)}>
                {displayedContent}
            </EnhancedText>
        );
    };

    render() {
        const {
            commentContentViewModel: { comment },
            styles,
        } = this.props;
        const { displayedContent } = comment;
        const isDeleted = displayedContent instanceof DeletedContent || displayedContent instanceof PurgedContent;

        let galleryProps = {
            key: 'gallery',
            limit: 4,
            items: comment.gallery,
            align: 'left',
        };

        if (comment.gallery && comment.gallery.length === 1) {
            let imageRatio = 1;
            let galleryStyles = photoswipeGalleryStyles;
            imageRatio = comment.gallery[0].height / comment.gallery[0].width;
            if (imageRatio > 1) {
                galleryStyles = portraitGalleryStyles;
            }

            galleryProps = {
                photoswipeGalleryStyles: galleryStyles,
                firstImageSizes: { md: 490, xlg: 540 },
                ...galleryProps,
            };
        }

        return (
            <div className={styles.commentContent}>
                <ViewerConsumer>
                    {({ viewer }) => (
                        <>
                            <p className={styles.text}>
                                {this.displayedContent()}
                                {viewer && viewer.isSuperstaff && comment.deleteReason ? (
                                    <span>{`: ${comment.deleteReason}`}</span>
                                ) : null}
                            </p>
                            {viewer && viewer.isSuperstaff && isDeleted ? <p>{comment.body}</p> : null}
                            {viewer && viewer.isSuperstaff && isDeleted ? (
                                <Mutation mutation={mutation}>
                                    {recoverFn => (
                                        <button
                                            onClick={() => {
                                                const globalId = toGlobalId('MessageNode', comment.id);

                                                recoverFn({
                                                    variables: {
                                                        input: {
                                                            id: globalId,
                                                        },
                                                    },
                                                }).then(() => {
                                                    comment.recover();
                                                });
                                            }}
                                            style={{ color: 'green' }}
                                        >
                                            Recover
                                        </button>
                                    )}
                                </Mutation>
                            ) : null}
                        </>
                    )}
                </ViewerConsumer>
                {comment.gallery && comment.gallery.length ? (
                    <TextShortener text={comment.body || ''} words={4}>
                        {text => <GalleryView altPrefix={`${text} - `} {...galleryProps} />}
                    </TextShortener>
                ) : null}
            </div>
        );
    }
}

export default CommentContentView;
